import { createAction } from 'redux-actions'
import ms from 'ms'
import summarizeEvent from '@segment/summarize-request'
import normalizeEvent from '@segment/normalize-event'
import SSE from '@segment/sse.js'
import objectValues from 'nested-object-values'

/**
 * Interval.
 */

const TICK_INTERVAL = ms('1s')

/**
 * SSE singleton.
 */

let sse = null

/**
 * Debugger "tick" interval id.
 */

let interval = -1

/**
 * Action types.
 */

export const DEBUGGER_RECEIVE_EVENT = 'DEBUGGER_RECEIVE_EVENT'
export const DEBUGGER_RECEIVE_HISTORICAL_EVENT =
  'DEBUGGER_RECEIVE_HISTORICAL_EVENT'
export const DEBUGGER_RECEIVE_ERROR = 'DEBUGGER_RECEIVE_ERROR'
export const DEBUGGER_RECEIVE_HISTORICAL_ERROR =
  'DEBUGGER_RECEIVE_HISTORICAL_ERROR'
export const DEBUGGER_TICK = 'DEBUGGER_TICK'
export const DEBUGGER_CREATING = 'DEBUGGER_CREATING'
export const DEBUGGER_CREATED = 'DEBUGGER_CREATED'
export const DEBUGGER_DESTROYED = 'DEBUGGER_DESTROYED'
export const DEBUGGER_PAUSED = 'DEBUGGER_PAUSED'
export const DEBUGGER_RESUMED = 'DEBUGGER_RESUMED'
export const DEBUGGER_SET_MEMORY_LIMIT = 'DEBUGGER_SET_MEMORY_LIMIT'
export const DEBUGGER_LOADED = 'DEBUGGER_LOADED'

export const SET_SOURCE_DEBUGGER_SETTINGS = 'SET_SOURCE_DEBUGGER_SETTINGS'
export const setSourceDebuggerSettings = createAction(
  SET_SOURCE_DEBUGGER_SETTINGS
)

/**
 * Actions.
 */

const debuggerReceiveEvent = createAction(DEBUGGER_RECEIVE_EVENT)
const debuggerReceiveHistoricalEvent = createAction(
  DEBUGGER_RECEIVE_HISTORICAL_EVENT
)
// Error reporting is currently broken
//
// const debuggerReceiveError = createAction(DEBUGGER_RECEIVE_ERROR)
// const debuggerReceiveHistoricalError = createAction(
//   DEBUGGER_RECEIVE_HISTORICAL_ERROR
// )
const debuggerTick = createAction(DEBUGGER_TICK)
const debuggerCreating = createAction(DEBUGGER_CREATING)
const debuggerCreated = createAction(DEBUGGER_CREATED)
const debuggerDestroyed = createAction(DEBUGGER_DESTROYED)
const debuggerPaused = createAction(DEBUGGER_PAUSED)
const debuggerResumed = createAction(DEBUGGER_RESUMED)
const debuggerLoaded = createAction(DEBUGGER_LOADED)

/**
 * Create a "debugger".
 */

export function debuggerInit({ workspaceSlug, sourceSlug }) {
  return function(dispatch) {
    dispatch(debuggerCreating())

    if (sse) {
      throw new Error('Debugger already initialized')
    }

    sse = new SSE(
      `/api/workspaces/${workspaceSlug}/projects/${sourceSlug}/stream`,
      { withCredentials: true }
    )

    sse.on('request', req =>
      dispatch(
        debuggerReceiveEvent({
          event: toEvent({ req, status: 'ALLOWED', isHistorical: false })
        })
      )
    )
    // Error reporting is currently broken
    //
    // sse.on('integration-error', req =>
    //   dispatch(debuggerReceiveError({ error: req }))
    // )
    sse.on('blocked-event', req =>
      dispatch(
        debuggerReceiveEvent({
          event: toEvent({ req, status: 'BLOCKED', isHistorical: false })
        })
      )
    )

    sse.on('request-historical', req =>
      dispatch(
        debuggerReceiveHistoricalEvent({
          event: toEvent({ req, status: 'ALLOWED', isHistorical: true })
        })
      )
    )
    // Error reporting is currently broken
    //
    // sse.on('integration-error-historical', req =>
    //   dispatch(debuggerReceiveHistoricalError({ error: req }))
    // )
    sse.on('blocked-event-historical', req =>
      dispatch(
        debuggerReceiveHistoricalEvent({
          event: toEvent({ req, status: 'BLOCKED', isHistorical: true })
        })
      )
    )

    sse.on('loaded', () => dispatch(debuggerLoaded()))

    interval = setInterval(() => dispatch(debuggerTick()), TICK_INTERVAL)

    dispatch(debuggerCreated())
  }
}

/**
 * Pause the debugger.
 */

export function debuggerPause() {
  return function(dispatch) {
    clearInterval(interval)
    dispatch(debuggerPaused())
  }
}

/**
 * Resume the debugger.
 */

export function debuggerResume() {
  return function(dispatch) {
    interval = setInterval(() => dispatch(debuggerTick()), TICK_INTERVAL)
    dispatch(debuggerResumed())
  }
}

/**
 * Destroy the debugger.
 */

export function debuggerDestroy() {
  return function(dispatch) {
    clearInterval(interval)
    sse.close()
    sse = null
    dispatch(debuggerDestroyed())
  }
}

/**
 * Set the memory limit.
 */

export function debuggerSetMemoryLimit(n) {
  const limit = parseInt(n, 10)
  if (isNaN(limit)) {
    throw new Error(`debugger memory limit must be a number. got "${n}"`)
  }

  return {
    type: DEBUGGER_SET_MEMORY_LIMIT,
    payload: limit
  }
}

/**
 * Request -> event.
 */

function toEvent({ req, status, isHistorical }) {
  const normalized = normalizeEvent(req)
  const normalizedValues = objectValues(normalized).map(value => String(value))
  const normalizedJson = JSON.stringify(normalized, null, 2)
  const summary = summarizeEvent(req)

  return {
    id: req.messageId,
    data: req,
    normalized,
    normalizedValues,
    normalizedJson,
    summary,
    errors: [],
    status,
    isHistorical
  }
}
